"use client";
/**
 * InvoiceUploadView3
 *
 * Invoice upload + OCR reader. Each card is either:
 *  - a Project bill (PO/MO/vendor or WO/subcontractor), or
 *  - an Overhead Ledger bill (fiscal year + ledger account + vendor).
 *
 * Overhead cards default to the active fiscal year's budget.
 */

import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import * as pdfjsLib from "pdfjs-dist";
import Tesseract from "tesseract.js";
import Select from "react-select";
import AlertDialog from "./AlertDialog";
import {
  extractInvoiceDataAI,
  matchVendorFromText,
  extractReceiptDataAI,
} from "./invoiceReader3";

pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.js";

const API = "https://erp-xe9u.onrender.com/api";

const authH = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const selectStyles = {
  control: (p, s) => ({
    ...p,
    backgroundColor: "#111827",
    borderColor: s.isFocused ? "#f97316" : "#374151",
    boxShadow: "none",
    minHeight: "34px",
    fontSize: "13px",
    "&:hover": { borderColor: "#f97316" },
  }),
  input: (p) => ({ ...p, color: "#e5e7eb" }),
  singleValue: (p) => ({ ...p, color: "#f3f4f6", fontSize: "13px" }),
  placeholder: (p) => ({ ...p, color: "#6b7280", fontSize: "13px" }),
  option: (p, s) => ({
    ...p,
    backgroundColor: s.isSelected
      ? "#92400e"
      : s.isFocused
        ? "#1f2937"
        : "#111827",
    color: s.isSelected ? "#fde68a" : "#e5e7eb",
    fontSize: "13px",
    padding: "7px 12px",
  }),
  menu: (p) => ({
    ...p,
    backgroundColor: "#111827",
    border: "1px solid #1f2937",
    zIndex: 9999,
  }),
  menuList: (p) => ({ ...p, padding: "4px", maxHeight: "220px" }),
  valueContainer: (p) => ({ ...p, padding: "0 8px" }),
  dropdownIndicator: (p) => ({ ...p, padding: "4px", color: "#4b5563" }),
  indicatorSeparator: (p) => ({ ...p, backgroundColor: "#1f2937" }),
};

const inputCls =
  "block w-full rounded bg-gray-800 border border-gray-700 text-gray-200 text-xs py-1.5 px-2 focus:border-orange-500 focus:outline-none transition-colors placeholder-gray-600";
const labelCls =
  "block text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1";

async function extractPdfText(file) {
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  let text = "";

  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const content = await page.getTextContent();

    const Y_TOL = 3;
    const rows = [];
    for (const item of content.items) {
      if (!item.str || !item.str.trim()) continue;
      const x = item.transform[4];
      const y = item.transform[5];
      let row = rows.find((r) => Math.abs(r.y - y) <= Y_TOL);
      if (!row) {
        row = { y, items: [] };
        rows.push(row);
      }
      row.items.push({ x, str: item.str });
    }
    rows.sort((a, b) => b.y - a.y);
    for (const row of rows) {
      row.items.sort((a, b) => a.x - b.x);
      text += row.items.map((it) => it.str).join(" ") + "\n";
    }
    text += "\n";
  }

  return text;
}

function computeMoTotal(mo) {
  if (!mo) return null;
  const itemsTotal = (mo.items || []).reduce(
    (s, i) => s + (parseFloat(i.total) || 0),
    0,
  );
  const surchargeTotal = (mo.surcharges || []).reduce(
    (s, sc) => s + (parseFloat(sc.price ?? sc.amount ?? 0) || 0),
    0,
  );
  return itemsTotal + surchargeTotal;
}

function findMoDuplicateGroups(invoices) {
  const byMo = {};
  invoices.forEach((inv, idx) => {
    if (inv.processing || !inv.relatedMO3Id) return;
    const key = inv.relatedMO3Id;
    if (!byMo[key]) byMo[key] = [];
    byMo[key].push(idx);
  });
  return Object.values(byMo).filter((idxs) => idxs.length > 1);
}

// Flattens a budget's categories into indented dropdown options
function flattenLedger(categories) {
  const active = (categories || []).filter((c) => c.active !== false);
  const byParent = new Map();
  for (const c of active) {
    const k = c.parentCategoryId ? String(c.parentCategoryId) : "root";
    if (!byParent.has(k)) byParent.set(k, []);
    byParent.get(k).push(c);
  }
  for (const l of byParent.values())
    l.sort((a, b) =>
      String(a.accountNumber).localeCompare(
        String(b.accountNumber),
        undefined,
        { numeric: true },
      ),
    );
  const out = [];
  const walk = (k, d) => {
    for (const c of byParent.get(k) || []) {
      out.push({
        value: c._id,
        label: `${"·  ".repeat(d)}${c.accountNumber} — ${c.name}`,
      });
      walk(String(c._id), d + 1);
    }
  };
  walk("root", 0);
  return out;
}

async function rasterizeFirstPage(file) {
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  const page = await pdf.getPage(1);
  const vp = page.getViewport({ scale: 1.5 });
  const canvas = document.createElement("canvas");
  canvas.width = vp.width;
  canvas.height = vp.height;
  await page.render({ canvasContext: canvas.getContext("2d"), viewport: vp })
    .promise;
  return canvas.toDataURL("image/jpeg", 0.8);
}

const Spinner = ({ label = "Processing…" }) => (
  <div className="flex items-center gap-2 text-xs text-orange-400">
    <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8v8H4z"
      />
    </svg>
    {label}
  </div>
);

const MergeSuggestionBanner = ({ group, invoices, onMerge, onDismiss }) => {
  const cards = group.idxs.map((i) => invoices[i]).filter(Boolean);
  if (cards.length < 2) return null;

  const moLabel = cards[0]?.moNumber
    ? `MO ${cards[0].moNumber}`
    : "the same Material Order";
  const combinedSubtotal = cards.reduce(
    (s, c) => s + (parseFloat(c.subtotal) || 0),
    0,
  );

  return (
    <div className="rounded-xl border border-indigo-700/50 bg-indigo-950/20 px-4 py-3 flex items-start gap-3">
      <span className="text-lg leading-none mt-0.5">🔗</span>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-indigo-200 font-medium">
          {cards.length} invoices match {moLabel} — likely one vendor order
          split across multiple invoices.
        </p>
        <p className="text-xs text-indigo-300/80 mt-0.5">
          {cards.map((c) => c.invoiceNumber || c.file.name).join(" + ")} →
          combined subtotal{" "}
          <span className="font-mono font-semibold">
            ${combinedSubtotal.toFixed(2)}
          </span>
        </p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          onClick={onMerge}
          className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white transition-colors"
        >
          Merge into one bill
        </button>
        <button
          onClick={onDismiss}
          className="px-2 py-1.5 text-xs text-indigo-400 hover:text-indigo-200 transition-colors"
        >
          Keep separate
        </button>
      </div>
    </div>
  );
};

function InvoiceCard({
  inv,
  index,
  v3Projects,
  vendors,
  subcontractors,
  mosByProject,
  billedMoIdsByProject,
  onUpdate,
  onRemove,
  onLoadMOs,
  defaultTaxRate = 0,
  budgets = [],
  defaultBudgetId = "",
  allInvoices = [],
  onMergeInto,
  mergeSourceId,
  setMergeSourceId,
}) {
  const [moSearch, setMoSearch] = useState("");

  const projectId = inv.projectId || "";
  const isWO = inv.isWO || false;
  const isOverhead = inv.destination === "overhead";

  const ovBudgetId = inv.overheadBudgetId || defaultBudgetId;
  const ovBudget = budgets.find((b) => String(b._id) === String(ovBudgetId));
  const budgetOpts = budgets.map((b) => ({
    value: b._id,
    label: b.label || `FY${b.fiscalYear}`,
  }));
  const ledgerOpts = flattenLedger(ovBudget?.categories);

  const proj = v3Projects.find((p) => String(p._id) === String(projectId));
  const billedMoIds = billedMoIdsByProject[projectId] || new Set();
  const poOpts = (proj?.purchaseOrders || []).map((po) => ({
    value: po._id,
    label: `PO ${po.poNumber}${po.title ? ` — ${po.title}` : ""}`,
  }));
  const woOpts = (proj?.workOrders || []).map((wo) => ({
    value: wo._id,
    label: `WO ${wo.woNumber}${wo.title ? ` — ${wo.title}` : ""}`,
  }));
  const moOpts = (mosByProject[projectId] || [])
    .filter((mo) => mo.status !== "void" && !billedMoIds.has(String(mo._id)))
    .map((mo) => {
      const poRef = mo.purchaseOrder;
      const poNum = poRef?.poNumber || "";
      return {
        value: mo._id,
        label: `MO ${mo.orderNumber}${poNum ? ` · PO ${poNum}` : ""} (${(mo.status || "").toUpperCase()})`,
        orderNumber: String(mo.orderNumber),
        status: mo.status,
        poId: poRef?._id || poRef || null,
        vendorId: mo.vendor?._id || mo.vendor || null,
      };
    });

  const sortedMoOpts = (() => {
    const statusPriority = { delivered: 0, partial: 1, pending: 2 };
    if (!moSearch) {
      return [...moOpts].sort((a, b) => {
        const sa = statusPriority[a.status] ?? 3;
        const sb = statusPriority[b.status] ?? 3;
        return sa !== sb
          ? sa - sb
          : a.orderNumber.localeCompare(b.orderNumber, undefined, {
              numeric: true,
            });
      });
    }

    const term = moSearch.trim().toLowerCase();
    return [...moOpts]
      .filter((o) => o.label.toLowerCase().includes(term))
      .sort((a, b) => {
        const aExact = a.orderNumber === term;
        const bExact = b.orderNumber === term;
        if (aExact !== bExact) return aExact ? -1 : 1;
        const aStarts = a.orderNumber.startsWith(term);
        const bStarts = b.orderNumber.startsWith(term);
        if (aStarts !== bStarts) return aStarts ? -1 : 1;
        const sa = statusPriority[a.status] ?? 3;
        const sb = statusPriority[b.status] ?? 3;
        return sa !== sb
          ? sa - sb
          : a.orderNumber.localeCompare(b.orderNumber, undefined, {
              numeric: true,
            });
      });
  })();

  const selectedMo = (mosByProject[projectId] || []).find(
    (mo) => String(mo._id) === String(inv.relatedMO3Id),
  );
  const moTotalForCompare = selectedMo ? computeMoTotal(selectedMo) : null;
  const billSubtotal = parseFloat(inv.subtotal) || 0;
  const moMismatch =
    moTotalForCompare != null &&
    Math.abs(billSubtotal - moTotalForCompare) >
      Math.abs(moTotalForCompare) * 0.03;

  const vendorOpts = vendors.map((v) => ({
    value: v._id,
    label: v.companyName,
  }));
  const subOpts = subcontractors.map((s) => ({
    value: s._id,
    label: s.displayName || "Unnamed",
  }));

  const set = (field, val) => onUpdate(index, { ...inv, [field]: val });

  const handleSubtotalChange = (value) => {
    const sub = parseFloat(value);
    if (isNaN(sub)) {
      onUpdate(index, { ...inv, subtotal: value === "" ? null : value });
      return;
    }
    const tax = +(sub * defaultTaxRate).toFixed(2);
    const total = +(sub + tax).toFixed(2);
    onUpdate(index, { ...inv, subtotal: sub, hst: tax, total });
  };

  const handleTaxChange = (value) => {
    const tax = parseFloat(value);
    const sub = parseFloat(inv.subtotal) || 0;
    if (isNaN(tax)) {
      onUpdate(index, { ...inv, hst: value === "" ? null : value });
      return;
    }
    const total = +(sub + tax).toFixed(2);
    onUpdate(index, { ...inv, hst: tax, total });
  };

  const handleMOChange = (sel) => {
    const updates = { ...inv, relatedMO3Id: sel?.value || "" };
    if (sel?.poId) updates.relatedPO3Id = String(sel.poId);
    if (sel?.vendorId) updates.payToId = String(sel.vendorId);
    onUpdate(index, updates);
  };

  const handleProjectChange = (sel) => {
    onUpdate(index, {
      ...inv,
      projectId: sel?.value || "",
      relatedPO3Id: "",
      relatedMO3Id: "",
      relatedWO3Id: "",
      payToId: "",
      subContractorId: "",
    });
    if (sel?.value) onLoadMOs(sel.value);
  };

  const handleOrderTypeChange = (nextIsWO) => {
    if (nextIsWO) {
      onUpdate(index, {
        ...inv,
        isWO: true,
        relatedPO3Id: "",
        relatedMO3Id: "",
        payToId: "",
      });
    } else {
      onUpdate(index, {
        ...inv,
        isWO: false,
        relatedWO3Id: "",
        subContractorId: "",
      });
    }
  };

  const handleDestinationChange = (dest) => {
    if (dest === "overhead") {
      onUpdate(index, {
        ...inv,
        destination: "overhead",
        overheadBudgetId: ovBudgetId,
        isWO: false,
        projectId: "",
        relatedPO3Id: "",
        relatedMO3Id: "",
        relatedWO3Id: "",
        subContractorId: "",
      });
    } else {
      onUpdate(index, {
        ...inv,
        destination: "project",
        overheadCategoryId: "",
      });
    }
  };

  const conf = inv.confidence || {};
  const ConfBadge = ({ field }) => {
    if (!conf[field]) return null;
    return (
      <span
        className={`ml-1 text-[9px] px-1 py-0.5 rounded font-medium ${
          conf[field] === "high"
            ? "bg-emerald-900/40 text-emerald-400"
            : conf[field] === "medium"
              ? "bg-amber-900/40 text-amber-400"
              : "bg-gray-700 text-gray-500"
        }`}
      >
        {conf[field] === "high" ? "✓" : conf[field] === "medium" ? "~" : "?"}
      </span>
    );
  };

  const projectSelectOpts = v3Projects.map((p) => ({
    value: p._id,
    label: p.projectNumber
      ? `${p.projectNumber} — ${p.projectName}`
      : p.projectName,
    projectNumber: p.projectNumber,
    projectName: p.projectName,
  }));

  const projectSelect = (
    <div>
      <label className={labelCls}>
        Project (V3) <span className="text-red-400">*</span>
      </label>
      <Select
        options={projectSelectOpts}
        value={projectSelectOpts.find((o) => o.value === projectId) || null}
        onChange={handleProjectChange}
        styles={selectStyles}
        isClearable
        placeholder={
          v3Projects.length === 0 ? "No V3 projects" : "Select project…"
        }
        formatOptionLabel={(opt) => (
          <div className="flex items-center gap-2">
            {opt.projectNumber && (
              <span className="text-orange-400 font-mono text-[11px] font-semibold">
                #{opt.projectNumber}
              </span>
            )}
            <span>{opt.projectName || opt.label}</span>
          </div>
        )}
      />
    </div>
  );

  return (
    <div
      className={`rounded-xl border overflow-hidden transition-all ${
        inv.processing
          ? "border-orange-700/60 bg-orange-950/10"
          : inv.error
            ? "border-amber-700/60 bg-amber-950/10"
            : "border-gray-700/60 bg-[#141824]"
      }`}
    >
      <div className="flex items-start gap-3 p-4">
        <div className="flex-shrink-0 w-16 h-20 rounded overflow-hidden bg-gray-800 border border-gray-700">
          {inv.preview ? (
            <img
              src={inv.preview}
              alt="preview"
              className="w-full h-full object-cover object-top"
            />
          ) : inv.processing ? (
            <div className="w-full h-full flex items-center justify-center">
              <svg
                className="w-4 h-4 text-orange-400 animate-spin"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v8H4z"
                />
              </svg>
            </div>
          ) : (
            <div className="w-full h-full flex items-center justify-center text-2xl">
              📄
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-medium text-gray-100 truncate max-w-xs">
              {inv.file.name}
            </p>
            {inv.processing && <Spinner label="Reading…" />}
            {inv.error && (
              <span className="text-xs text-amber-400">⚠ {inv.error}</span>
            )}
          </div>

          {!inv.processing && (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5">
              {inv.invoiceNumber && (
                <span className="text-[11px] text-gray-400">
                  <span className="text-gray-600">INV#</span>{" "}
                  <span className="text-gray-200 font-mono">
                    {inv.invoiceNumber}
                  </span>
                  <ConfBadge field="invoiceNumber" />
                </span>
              )}
              {inv.poNumber && (
                <span className="text-[11px] text-gray-400">
                  <span className="text-gray-600">PO</span>{" "}
                  <span className="text-orange-300 font-mono font-semibold">
                    {inv.poNumber}
                  </span>
                  <ConfBadge field="poNumber" />
                </span>
              )}
              {inv.total != null && (
                <span className="text-[11px] text-gray-400">
                  <span className="text-gray-600">Total</span>{" "}
                  <span className="text-green-300 font-mono font-semibold">
                    ${Number(inv.total).toFixed(2)}
                  </span>
                  <ConfBadge field="total" />
                </span>
              )}
              {inv.invoiceDate && (
                <span className="text-[11px] text-gray-500">
                  {inv.invoiceDate}
                </span>
              )}
              {isOverhead && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-orange-900/40 text-orange-300 font-medium">
                  Overhead
                </span>
              )}
              {inv.invoiceNumbers?.length > 1 && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-900/40 text-indigo-300 font-medium">
                  {inv.invoiceNumbers.length} invoices merged
                </span>
              )}
            </div>
          )}
        </div>

        {!inv.processing && allInvoices.length > 1 && (
          <div className="flex-shrink-0 relative">
            <button
              onClick={() =>
                setMergeSourceId((p) => (p === inv.id ? null : inv.id))
              }
              className="text-[10px] px-2 py-1 rounded border border-gray-700 text-gray-400 hover:text-orange-300 hover:border-orange-600 transition-colors whitespace-nowrap"
              title="Merge this invoice into another card as one bill"
            >
              🔗 Merge
            </button>
            {mergeSourceId === inv.id && (
              <div className="absolute right-0 top-full mt-1 z-20 w-56 bg-gray-800 border border-gray-700 rounded-lg shadow-xl py-1">
                <p className="px-3 py-1.5 text-[10px] text-gray-500 uppercase tracking-wide border-b border-gray-700">
                  Merge into…
                </p>
                {allInvoices
                  .filter((other) => other.id !== inv.id && !other.processing)
                  .map((other) => (
                    <button
                      key={other.id}
                      onClick={() => onMergeInto(index, other.index)}
                      className="w-full text-left px-3 py-1.5 text-xs text-gray-300 hover:bg-gray-700 transition-colors truncate"
                    >
                      {other.invoiceNumber || other.file.name}
                    </button>
                  ))}
                {allInvoices.filter((o) => o.id !== inv.id && !o.processing)
                  .length === 0 && (
                  <p className="px-3 py-1.5 text-xs text-gray-600 italic">
                    No other cards ready to merge
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        <button
          onClick={() => onRemove(index)}
          className="text-gray-600 hover:text-red-400 transition-colors text-base leading-none flex-shrink-0"
        >
          ✕
        </button>
      </div>

      {!inv.processing && (
        <div className="border-t border-gray-800 px-4 py-4 min-h-[400px] space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <label className={labelCls}>
                Invoice # <ConfBadge field="invoiceNumber" />
              </label>
              <input
                className={inputCls}
                value={inv.invoiceNumber || ""}
                onChange={(e) => set("invoiceNumber", e.target.value)}
                placeholder="INV-001"
              />
            </div>
            <div>
              <label className={labelCls}>
                Invoice Date <ConfBadge field="invoiceDate" />
              </label>
              <input
                type="date"
                className={inputCls}
                value={inv.invoiceDate || ""}
                onChange={(e) => set("invoiceDate", e.target.value)}
              />
            </div>
            <div>
              <label className={labelCls}>Due Date</label>
              <input
                type="date"
                className={inputCls}
                value={inv.dueDate || ""}
                onChange={(e) => set("dueDate", e.target.value)}
              />
            </div>
            <div>
              <label className={labelCls}>Invoice Status</label>
              <select
                className={inputCls}
                value={inv.invoiceStatus || "unpaid"}
                onChange={(e) => set("invoiceStatus", e.target.value)}
              >
                <option value="unpaid">Unpaid</option>
                <option value="paid">Paid</option>
                <option value="partial">Partial</option>
              </select>
            </div>
          </div>

          {/* ── Destination ── */}
          <div>
            <label className={labelCls}>Bill Destination</label>
            <div className="flex gap-2">
              {[
                { label: "Project Bill", value: "project" },
                { label: "Overhead Ledger", value: "overhead" },
              ].map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => handleDestinationChange(opt.value)}
                  className={`px-3 py-1.5 text-xs rounded-lg border font-medium transition-colors ${
                    (inv.destination || "project") === opt.value
                      ? "bg-orange-600 border-orange-500 text-white"
                      : "bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-500"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {isOverhead ? (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className={labelCls}>
                  Fiscal Year <span className="text-red-400">*</span>
                </label>
                <Select
                  options={budgetOpts}
                  value={budgetOpts.find((o) => o.value === ovBudgetId) || null}
                  onChange={(sel) =>
                    onUpdate(index, {
                      ...inv,
                      overheadBudgetId: sel?.value || "",
                      overheadCategoryId: "",
                    })
                  }
                  styles={selectStyles}
                  isSearchable={false}
                  placeholder={
                    budgets.length ? "Select year…" : "No overhead budgets"
                  }
                />
              </div>
              <div>
                <label className={labelCls}>
                  Ledger Account <span className="text-red-400">*</span>
                </label>
                <Select
                  options={ledgerOpts}
                  value={
                    ledgerOpts.find(
                      (o) => o.value === inv.overheadCategoryId,
                    ) || null
                  }
                  onChange={(sel) =>
                    set("overheadCategoryId", sel?.value || "")
                  }
                  styles={selectStyles}
                  isClearable
                  isDisabled={!ovBudgetId}
                  placeholder={
                    !ovBudgetId ? "Pick a year first" : "Select account…"
                  }
                />
              </div>
              <div>
                <label className={labelCls}>
                  Vendor <ConfBadge field="vendorId" />
                </label>
                <Select
                  options={vendorOpts}
                  value={
                    vendorOpts.find((o) => o.value === inv.payToId) || null
                  }
                  onChange={(sel) => set("payToId", sel?.value || "")}
                  styles={selectStyles}
                  isClearable
                  placeholder="Select vendor…"
                />
              </div>
            </div>
          ) : (
            <>
              <div>
                <label className={labelCls}>Order Type</label>
                <div className="flex gap-2">
                  {[
                    { label: "Purchase Order", value: false },
                    { label: "Work Order", value: true },
                  ].map((opt) => (
                    <button
                      key={String(opt.value)}
                      type="button"
                      onClick={() => handleOrderTypeChange(opt.value)}
                      className={`px-3 py-1.5 text-xs rounded-lg border font-medium transition-colors ${
                        isWO === opt.value
                          ? "bg-orange-600 border-orange-500 text-white"
                          : "bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-500"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {isWO ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  {projectSelect}
                  <div>
                    <label className={labelCls}>Work Order (WO3)</label>
                    <Select
                      options={woOpts}
                      value={
                        woOpts.find((o) => o.value === inv.relatedWO3Id) || null
                      }
                      onChange={(sel) => set("relatedWO3Id", sel?.value || "")}
                      styles={selectStyles}
                      isClearable
                      placeholder={
                        !projectId
                          ? "Select project first"
                          : woOpts.length === 0
                            ? "No WOs"
                            : "Select WO3…"
                      }
                      isDisabled={!projectId}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Subcontractor</label>
                    <Select
                      options={subOpts}
                      value={
                        subOpts.find((o) => o.value === inv.subContractorId) ||
                        null
                      }
                      onChange={(sel) =>
                        set("subContractorId", sel?.value || "")
                      }
                      styles={selectStyles}
                      isClearable
                      placeholder="Select subcontractor…"
                    />
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  {projectSelect}
                  <div>
                    <label className={labelCls}>
                      Material Order <ConfBadge field="poNumber" />
                    </label>
                    <Select
                      options={sortedMoOpts}
                      value={
                        moOpts.find((o) => o.value === inv.relatedMO3Id) || null
                      }
                      onInputChange={(val) => setMoSearch(val)}
                      filterOption={() => true}
                      onChange={handleMOChange}
                      styles={selectStyles}
                      isClearable
                      placeholder={
                        !projectId
                          ? "Select project first"
                          : moOpts.length === 0
                            ? "No MOs"
                            : "Select MO3…"
                      }
                      isDisabled={!projectId}
                      formatOptionLabel={(opt) => (
                        <div className="flex items-center justify-between">
                          <span>{opt.label}</span>
                          {opt.status === "delivered" && (
                            <span className="text-[9px] text-emerald-400 font-semibold ml-2">
                              ✓ DELIVERED
                            </span>
                          )}
                        </div>
                      )}
                    />
                    {inv.relatedMO3Id && inv.projectId && (
                      <Link
                        to={
                          "/Projects/" +
                          inv.projectId +
                          "/material-orders/" +
                          inv.relatedMO3Id
                        }
                        target="_blank"
                        className="inline-block mt-1 text-[10px] text-orange-400 hover:text-orange-300 transition-colors"
                      >
                        ↗ Open MO {inv.moNumber}
                      </Link>
                    )}
                  </div>
                  <div>
                    <label className={labelCls}>
                      PO3{" "}
                      <span className="text-gray-600 font-normal normal-case">
                        (auto-filled by MO)
                      </span>
                    </label>
                    <Select
                      options={poOpts}
                      value={
                        poOpts.find((o) => o.value === inv.relatedPO3Id) || null
                      }
                      onChange={(sel) => set("relatedPO3Id", sel?.value || "")}
                      styles={selectStyles}
                      isClearable
                      placeholder={!projectId ? "Select project first" : "PO3…"}
                      isDisabled={!projectId}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>
                      Vendor <ConfBadge field="vendorId" />
                    </label>
                    <Select
                      options={vendorOpts}
                      value={
                        vendorOpts.find((o) => o.value === inv.payToId) || null
                      }
                      onChange={(sel) => set("payToId", sel?.value || "")}
                      styles={selectStyles}
                      isClearable
                      placeholder="Select vendor…"
                    />
                  </div>
                </div>
              )}
            </>
          )}

          <div className="grid grid-cols-3 gap-3 bg-gray-900/50 rounded-lg p-3">
            <div>
              <label className={labelCls}>
                Subtotal <ConfBadge field="subtotal" />
              </label>
              <input
                type="number"
                step="0.01"
                className={inputCls}
                value={inv.subtotal ?? ""}
                onChange={(e) => handleSubtotalChange(e.target.value)}
                placeholder="0.00"
              />
            </div>
            <div>
              <label className={labelCls}>
                HST <ConfBadge field="hst" />
              </label>
              <input
                type="number"
                step="0.01"
                className={inputCls}
                value={inv.hst ?? ""}
                onChange={(e) => handleTaxChange(e.target.value)}
                placeholder="0.00"
              />
            </div>
            <div>
              <label className={labelCls}>
                Total <ConfBadge field="total" />
              </label>
              <input
                type="number"
                step="0.01"
                className={inputCls}
                value={inv.total ?? ""}
                onChange={(e) =>
                  set("total", parseFloat(e.target.value) || null)
                }
                placeholder="0.00"
              />
            </div>
          </div>

          {!isOverhead && !isWO && inv.poNumber && inv.moNumber && (
            <div className="bg-orange-950/20 border border-orange-700/30 rounded-lg px-3 py-2 text-xs text-orange-300 flex items-center justify-between">
              <span>
                <span className="font-semibold">Extracted PO:</span> Project{" "}
                <span className="font-mono">{inv.projectNumber}</span>, MO{" "}
                <span className="font-mono">{inv.moNumber}</span>
                {inv.relatedMO3Id ? (
                  <span className="ml-2 text-emerald-400">✓ MO matched</span>
                ) : (
                  <span className="ml-2 text-amber-400">
                    — Select MO above to confirm
                  </span>
                )}
              </span>
              {inv.relatedMO3Id && inv.projectId && (
                <Link
                  to={
                    "/Projects/" +
                    inv.projectId +
                    "/material-orders/" +
                    inv.relatedMO3Id
                  }
                  target="_blank"
                  className="ml-3 px-2 py-0.5 text-[10px] font-semibold bg-orange-800/40 hover:bg-orange-700/60 text-orange-200 rounded border border-orange-700/40 transition-colors whitespace-nowrap"
                >
                  View MO →
                </Link>
              )}
            </div>
          )}

          {!isOverhead &&
            !isWO &&
            inv.relatedMO3Id &&
            moTotalForCompare != null && (
              <div
                className={`rounded-lg px-3 py-2 text-xs flex items-start gap-2 border ${
                  moMismatch
                    ? "bg-red-950/20 border-red-700/40 text-red-300"
                    : "bg-emerald-950/20 border-emerald-700/30 text-emerald-300"
                }`}
              >
                <span className="text-sm leading-none mt-0.5">
                  {moMismatch ? "⚠" : "✓"}
                </span>
                <span>
                  {inv.invoiceNumbers?.length > 1 ? "Combined" : "Bill"}{" "}
                  subtotal{" "}
                  <span className="font-mono font-semibold">
                    ${billSubtotal.toFixed(2)}
                  </span>
                  {moMismatch ? " doesn't match " : " matches "}
                  MO total incl. surcharges{" "}
                  <span className="font-mono font-semibold">
                    ${moTotalForCompare.toFixed(2)}
                  </span>
                  {moMismatch && (
                    <span className="block mt-0.5 text-red-400/80">
                      Difference: $
                      {Math.abs(billSubtotal - moTotalForCompare).toFixed(2)} —
                      outside the 3% tolerance used at confirm time.
                      {inv.invoiceNumbers?.length > 1
                        ? " If the vendor has more invoices for this order, merge them in before saving."
                        : " If the vendor split this order across multiple invoices, use 🔗 Merge once they're both uploaded."}
                    </span>
                  )}
                </span>
              </div>
            )}
        </div>
      )}
    </div>
  );
}

export default function InvoiceUploadView3() {
  const [invoices, setInvoices] = useState([]);
  const [billedMoIdsByProject, setBilledMoIdsByProject] = useState({});
  const [v3Projects, setV3Projects] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [subcontractors, setSubcontractors] = useState([]);
  const [mosByProject, setMosByProject] = useState({});
  const [saving, setSaving] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [mergeSourceId, setMergeSourceId] = useState(null);
  const [mergeSuggestions, setMergeSuggestions] = useState([]);
  const [dismissedSuggestions, setDismissedSuggestions] = useState(new Set());
  const [defaultTaxRate, setDefaultTaxRate] = useState(0);
  const [budgets, setBudgets] = useState([]);
  const [defaultBudgetId, setDefaultBudgetId] = useState("");

  const dropRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const [alert, setAlert] = useState({
    open: false,
    type: "info",
    title: "",
    message: "",
  });
  const showAlert = (type, title, message) =>
    setAlert({
      open: true,
      type,
      title,
      message,
      onConfirm: () => setAlert((p) => ({ ...p, open: false })),
    });

  useEffect(() => {
    (async () => {
      try {
        const [optsRes, vendRes, subRes] = await Promise.all([
          axios.get(`${API}/projects/optionsV2`, { headers: authH() }),
          axios.get(`${API}/accounts/vendors`, { headers: authH() }),
          axios.get(`${API}/users/subcontractors`, { headers: authH() }),
        ]);
        setV3Projects((optsRes.data || []).filter((p) => p.version === 3));
        setVendors(vendRes.data || []);
        setSubcontractors(subRes.data || []);
      } catch {}
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/company-profile/tax-rate`, {
          headers: authH(),
        });
        let rate = parseFloat(res.data.taxRate) || 0;
        if (rate > 1) rate = rate / 100;
        setDefaultTaxRate(rate);
      } catch {
        setDefaultTaxRate(0);
      }
    })();
  }, []);

  // Overhead budgets — default to the active budget for the current year
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/overhead/budgets`, {
          headers: authH(),
        });
        const list = res.data.budgets || [];
        setBudgets(list);
        const yr = new Date().getFullYear();
        const def =
          list.find((b) => b.status === "active" && b.fiscalYear === yr) ||
          list.find((b) => b.fiscalYear === yr) ||
          list.find((b) => b.status === "active") ||
          list[0];
        if (def) setDefaultBudgetId(String(def._id));
      } catch {
        setBudgets([]);
      }
    })();
  }, []);

  useEffect(() => {
    const groups = findMoDuplicateGroups(invoices);
    const keyed = groups.map((idxs) => ({
      idxs,
      key: idxs
        .map((i) => invoices[i]?.id)
        .sort()
        .join("|"),
    }));
    setMergeSuggestions(keyed.filter((g) => !dismissedSuggestions.has(g.key)));
  }, [invoices, dismissedSuggestions]);

  const loadMOs = useCallback(
    async (projectId) => {
      if (!projectId || mosByProject[projectId]) return;
      try {
        const [moRes, billsRes] = await Promise.all([
          axios.get(`${API}/materialOrders3/project/${projectId}`, {
            headers: authH(),
          }),
          axios.get(`${API}/bills3`, {
            headers: authH(),
            params: {
              project: projectId,
              processingStatus: "processed",
              limit: 500,
            },
          }),
        ]);

        setMosByProject((p) => ({
          ...p,
          [projectId]: moRes.data.orders || [],
        }));

        const billedIds = new Set(
          (billsRes.data.bills || [])
            .map((b) => b.relatedMO3?._id || b.relatedMO3)
            .filter(Boolean)
            .map(String),
        );
        setBilledMoIdsByProject((p) => ({ ...p, [projectId]: billedIds }));
      } catch {
        setMosByProject((p) => ({ ...p, [projectId]: [] }));
        setBilledMoIdsByProject((p) => ({ ...p, [projectId]: new Set() }));
      }
    },
    [mosByProject],
  );

  const processFile = useCallback(
    async (file, id) => {
      setInvoices((prev) =>
        prev.map((inv) =>
          inv.id === id ? { ...inv, processing: true, error: null } : inv,
        ),
      );

      try {
        const isPDF = file.type === "application/pdf";
        let text = "";
        let preview = null;

        if (isPDF) {
          text = await extractPdfText(file);
          try {
            preview = await rasterizeFirstPage(file);
          } catch {}
          if (text.trim().length < 50) {
            const imageToOCR = preview ?? (await rasterizeFirstPage(file));
            const { data } = await Tesseract.recognize(imageToOCR, "eng");
            text = data.text;
          }
        } else {
          preview = URL.createObjectURL(file);
          const { data } = await Tesseract.recognize(preview, "eng");
          text = data.text;
        }

        const extracted = await extractInvoiceDataAI(text);
        if (extracted.error) {
          console.warn(
            "[InvoiceUploadView3] AI extraction error:",
            extracted.error,
          );
        }

        const isSparse =
          (!extracted.total && !extracted.invoiceNumber) || !extracted.poNumber;
        if (isSparse) {
          const receiptData = await extractReceiptDataAI(text);
          Object.keys(receiptData).forEach((k) => {
            if (receiptData[k] != null && !extracted[k])
              extracted[k] = receiptData[k];
          });
        }

        const confidence = {};
        if (extracted.invoiceNumber) confidence.invoiceNumber = "high";
        if (extracted.poNumber) confidence.poNumber = "high";
        if (extracted.total) confidence.total = "high";
        if (extracted.subtotal) confidence.subtotal = "high";
        if (extracted.hst) confidence.hst = "high";
        if (extracted.invoiceDate) confidence.invoiceDate = "high";

        const vendorId = matchVendorFromText(
          text,
          extracted.vendorName,
          vendors,
        );
        if (vendorId) confidence.vendorId = "high";

        let autoProjectId = null;
        let autoRelatedPO3 = null;
        let autoRelatedMO3 = null;
        let autoVendorId = vendorId;

        if (extracted.poNumber) {
          try {
            const lookupRes = await axios.get(
              `${API}/projects/lookupV3/${encodeURIComponent(extracted.poNumber)}`,
              { headers: authH() },
            );
            const { project: proj, mo, po, vendor: v } = lookupRes.data;
            autoProjectId = String(proj._id);
            autoRelatedMO3 = String(mo._id);
            autoRelatedPO3 = po ? String(po._id) : null;
            autoVendorId = v ? String(v._id) : vendorId;

            if (!mosByProject[autoProjectId]) {
              try {
                const moRes = await axios.get(
                  `${API}/materialOrders3/project/${autoProjectId}`,
                  { headers: authH() },
                );
                setMosByProject((p) => ({
                  ...p,
                  [autoProjectId]: moRes.data.orders || [],
                }));
              } catch {
                setMosByProject((p) => ({ ...p, [autoProjectId]: [] }));
              }
            }
          } catch (e) {
            console.warn("[InvoiceUploadView3] lookupV3 failed:", e.message);
          }
        }

        let finalSubtotal = extracted.subtotal;
        let finalHst = extracted.hst;
        let finalTotal = extracted.total;
        if (finalSubtotal != null && finalHst == null && finalTotal == null) {
          finalHst = +(finalSubtotal * defaultTaxRate).toFixed(2);
          finalTotal = +(finalSubtotal + finalHst).toFixed(2);
        }

        setInvoices((prev) =>
          prev.map((inv) =>
            inv.id !== id
              ? inv
              : {
                  ...inv,
                  processing: false,
                  preview,
                  rawText: text,
                  error: null,
                  confidence,
                  invoiceNumber: extracted.invoiceNumber || "",
                  poNumber: extracted.poNumber || "",
                  projectNumber: extracted.projectNumber || "",
                  moNumber: extracted.moNumber || "",
                  invoiceDate: extracted.invoiceDate || "",
                  dueDate: extracted.dueDate || "",
                  subtotal: finalSubtotal,
                  hst: finalHst,
                  total: finalTotal,
                  vendorName: extracted.vendorName || "",
                  // keep whatever destination the user chose while reading
                  isWO: false,
                  projectId: autoProjectId || "",
                  relatedMO3Id: autoRelatedMO3 || "",
                  relatedPO3Id: autoRelatedPO3 || "",
                  relatedWO3Id: "",
                  payToId: autoVendorId || "",
                  subContractorId: "",
                  invoiceStatus: "unpaid",
                },
          ),
        );
      } catch (err) {
        setInvoices((prev) =>
          prev.map((inv) =>
            inv.id !== id
              ? inv
              : {
                  ...inv,
                  processing: false,
                  error: `Read failed: ${err.message}`,
                },
          ),
        );
      }
    },
    [v3Projects, vendors, mosByProject, defaultTaxRate],
  );

  const handleFiles = useCallback(
    (files) => {
      const allowed = [
        "application/pdf",
        "image/png",
        "image/jpeg",
        "image/jpg",
      ];
      const valid = files.filter((f) => allowed.includes(f.type));
      if (valid.length !== files.length) {
        showAlert(
          "warning",
          "Unsupported Files",
          "Only PDFs and images are accepted.",
        );
      }
      const newEntries = valid.map((file) => ({
        id: `${file.name}-${Date.now()}-${Math.random()}`,
        file,
        processing: true,
        error: null,
        preview: null,
        rawText: "",
        confidence: {},
        invoiceNumber: "",
        poNumber: "",
        projectNumber: "",
        moNumber: "",
        invoiceDate: "",
        dueDate: "",
        subtotal: null,
        hst: null,
        total: null,
        vendorName: "",
        isWO: false,
        destination: "project",
        overheadBudgetId: defaultBudgetId,
        overheadCategoryId: "",
        projectId: "",
        relatedMO3Id: "",
        relatedPO3Id: "",
        relatedWO3Id: "",
        payToId: "",
        subContractorId: "",
        invoiceStatus: "unpaid",
      }));
      setInvoices((prev) => [...prev, ...newEntries]);
      newEntries.forEach((e) => processFile(e.file, e.id));
    },
    [processFile, defaultBudgetId],
  );

  const updateInvoice = (index, updated) =>
    setInvoices((prev) => prev.map((inv, i) => (i === index ? updated : inv)));

  const removeInvoice = (index) =>
    setInvoices((prev) => prev.filter((_, i) => i !== index));

  const mergeInvoices = (targetIndex, sourceIndex) => {
    setInvoices((prev) => {
      const target = prev[targetIndex];
      const source = prev[sourceIndex];
      if (!target || !source || targetIndex === sourceIndex) return prev;

      const mergedInvoiceNumbers = [
        ...(target.invoiceNumbers?.length
          ? target.invoiceNumbers
          : [target.invoiceNumber].filter(Boolean)),
        ...(source.invoiceNumbers?.length
          ? source.invoiceNumbers
          : [source.invoiceNumber].filter(Boolean)),
      ].filter(Boolean);

      const mergedFiles = [
        ...(target.mergedFiles?.length ? target.mergedFiles : [target.file]),
        ...(source.mergedFiles?.length ? source.mergedFiles : [source.file]),
      ];

      const sum = (a, b) => (a ?? 0) + (b ?? 0);

      const merged = {
        ...target,
        invoiceNumbers: mergedInvoiceNumbers,
        invoiceNumber: mergedInvoiceNumbers.join(" "),
        mergedFiles,
        subtotal: sum(target.subtotal, source.subtotal),
        hst: sum(target.hst, source.hst),
        total: sum(target.total, source.total),
      };

      return prev
        .map((inv, i) => (i === targetIndex ? merged : inv))
        .filter((_, i) => i !== sourceIndex);
    });
    setMergeSourceId(null);
  };

  const mergeSuggestionGroup = (group) => {
    setInvoices((prev) => {
      const ids = group.idxs.map((i) => prev[i]?.id).filter(Boolean);
      const cards = ids
        .map((id) => prev.find((inv) => inv.id === id))
        .filter(Boolean);
      if (cards.length < 2) return prev;

      const [first, ...rest] = cards;

      const mergedInvoiceNumbers = cards.flatMap((c) =>
        c.invoiceNumbers?.length
          ? c.invoiceNumbers
          : [c.invoiceNumber].filter(Boolean),
      );
      const mergedFiles = cards.flatMap((c) =>
        c.mergedFiles?.length ? c.mergedFiles : [c.file],
      );
      const sum = (field) => cards.reduce((s, c) => s + (c[field] ?? 0), 0);

      const merged = {
        ...first,
        invoiceNumbers: mergedInvoiceNumbers,
        invoiceNumber: mergedInvoiceNumbers.join(" "),
        mergedFiles,
        subtotal: sum("subtotal"),
        hst: sum("hst"),
        total: sum("total"),
      };

      const restIds = new Set(rest.map((c) => c.id));
      return prev
        .map((inv) => (inv.id === first.id ? merged : inv))
        .filter((inv) => !restIds.has(inv.id));
    });
    setDismissedSuggestions((prev) => new Set(prev).add(group.key));
  };

  const dismissSuggestion = (key) =>
    setDismissedSuggestions((prev) => new Set(prev).add(key));

  const isReady = (inv) =>
    !inv.processing &&
    !!inv.total &&
    (inv.destination === "overhead"
      ? !!(inv.overheadBudgetId || defaultBudgetId) && !!inv.overheadCategoryId
      : !!inv.projectId);

  const handleSave = async () => {
    const ready = invoices.filter(isReady);
    if (!ready.length) {
      showAlert(
        "error",
        "Nothing to Save",
        "Each bill needs a total plus either a project, or a fiscal year and ledger account.",
      );
      return;
    }
    setSaving(true);
    const savedIds = new Set();
    let failed = 0;
    for (const inv of ready) {
      try {
        const overhead = inv.destination === "overhead";
        const budgetId = inv.overheadBudgetId || defaultBudgetId;
        const fd = new FormData();
        fd.append("billName", inv.invoiceNumber || inv.file.name);
        fd.append("invoiceNumber", inv.invoiceNumber || `MANUAL-${Date.now()}`);
        fd.append(
          "invoiceDate",
          inv.invoiceDate || new Date().toISOString().split("T")[0],
        );
        if (inv.dueDate) fd.append("dueDate", inv.dueDate);
        fd.append("invoiceStatus", inv.invoiceStatus || "unpaid");
        fd.append("subtotal", inv.subtotal ?? inv.total ?? 0);
        fd.append("tax", inv.hst ?? 0);
        fd.append("total", inv.total ?? 0);

        if (overhead) {
          fd.append("overheadBudgetId", budgetId);
          fd.append("overheadCategoryId", inv.overheadCategoryId);
          if (inv.payToId) fd.append("payTo", inv.payToId);
        } else {
          fd.append("project", inv.projectId);
          if (inv.isWO) {
            if (inv.relatedWO3Id) fd.append("relatedWO3", inv.relatedWO3Id);
            if (inv.subContractorId)
              fd.append("subContractor", inv.subContractorId);
          } else {
            if (inv.relatedPO3Id) fd.append("relatedPO3", inv.relatedPO3Id);
            if (inv.relatedMO3Id) fd.append("relatedMO3", inv.relatedMO3Id);
            if (inv.payToId) fd.append("payTo", inv.payToId);
          }
        }

        const filesToSend = inv.mergedFiles?.length
          ? inv.mergedFiles
          : [inv.file];
        filesToSend.forEach((f) => fd.append("billFiles", f));

        const res = await axios.post(`${API}/bills3`, fd, {
          headers: { ...authH(), "Content-Type": "multipart/form-data" },
        });

        // Safety net: if bills3 didn't persist the overhead tags, post it now.
        const created = res.data?.bill || res.data;
        if (
          overhead &&
          created?._id &&
          String(created.overheadCategoryId || "") !==
            String(inv.overheadCategoryId)
        ) {
          await axios.post(
            `${API}/overhead/bills/${created._id}/post`,
            {
              overheadBudgetId: budgetId,
              overheadCategoryId: inv.overheadCategoryId,
            },
            { headers: authH() },
          );
        }
        savedIds.add(inv.id);
      } catch {
        failed++;
      }
    }
    setSaving(false);
    if (savedIds.size > 0) {
      setInvoices((prev) => prev.filter((inv) => !savedIds.has(inv.id)));
      showAlert(
        "success",
        "Saved",
        `${savedIds.size} bill${savedIds.size !== 1 ? "s" : ""} saved.${failed ? ` ${failed} failed and were kept on screen.` : ""}`,
      );
    } else {
      showAlert(
        "error",
        "Save Failed",
        "All bills failed to save. Check console for details.",
      );
    }
  };

  const readyCount = invoices.filter(isReady).length;

  return (
    <div className="min-h-screen bg-[#0d1117] text-gray-100">
      <AlertDialog
        open={alert.open}
        type={alert.type}
        title={alert.title}
        message={alert.message}
        confirmText="OK"
        cancelText="Close"
        onClose={() => setAlert((p) => ({ ...p, open: false }))}
        onConfirm={alert.onConfirm}
      />

      <div className="border-b border-gray-800 bg-[#0d1117]/95 backdrop-blur sticky top-0 z-30">
        <div className="mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
          <div>
            <h1 className="text-lg font-semibold text-white tracking-tight">
              Invoice Upload
            </h1>
            <p className="text-xs text-gray-500 mt-0.5">
              {invoices.length === 0
                ? "Upload invoices — PO numbers are read automatically"
                : `${invoices.length} invoice${invoices.length !== 1 ? "s" : ""} · ${readyCount} ready`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate(-1)}
              className="px-3 py-1.5 text-xs text-gray-400 hover:text-white border border-gray-700 rounded-lg transition-colors"
            >
              ← Back
            </button>
            {invoices.length > 0 && (
              <button
                onClick={() => setInvoices([])}
                className="px-3 py-1.5 text-xs text-gray-400 hover:text-white border border-gray-700 rounded-lg transition-colors"
              >
                Clear all
              </button>
            )}
            <button
              onClick={handleSave}
              disabled={saving || readyCount === 0}
              className="px-4 py-2 text-sm font-semibold bg-orange-500 hover:bg-orange-400 text-white rounded-lg transition-colors disabled:opacity-40 shadow-lg shadow-orange-500/20"
            >
              {saving
                ? "Saving…"
                : `Save${readyCount > 0 ? ` (${readyCount})` : ""}`}
            </button>
          </div>
        </div>
      </div>

      <div className="mx-auto px-4 sm:px-6 py-6 space-y-4">
        <div
          ref={dropRef}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            handleFiles(Array.from(e.dataTransfer.files));
          }}
          onClick={() => inputRef.current?.click()}
          className={`rounded-xl border-2 border-dashed cursor-pointer px-8 py-10 text-center transition-all
            ${dragging ? "border-orange-500 bg-orange-500/5" : "border-gray-700 hover:border-gray-500 bg-[#141824] hover:bg-[#1a1f2e]"}`}
        >
          <input
            ref={inputRef}
            type="file"
            multiple
            accept="application/pdf,image/*"
            className="hidden"
            onChange={(e) => {
              handleFiles(Array.from(e.target.files));
              e.target.value = null;
            }}
          />
          <p className="text-sm font-medium text-gray-300">
            {dragging
              ? "Release to upload"
              : "Drop invoices here · Click to browse"}
          </p>
          <p className="text-xs text-gray-600 mt-1">
            PDF text extracted automatically · PO number{" "}
            <span className="font-mono text-orange-400">87-839</span> → Project
            87, MO 839
          </p>
        </div>

        {invoices.length > 0 && (
          <div className="grid grid-cols-3 gap-3">
            {[
              {
                label: "Total",
                value: invoices.length,
                color: "text-gray-200",
              },
              { label: "Ready", value: readyCount, color: "text-emerald-400" },
              {
                label: "Processing",
                value: invoices.filter((i) => i.processing).length,
                color: "text-orange-400",
              },
            ].map((s) => (
              <div
                key={s.label}
                className="rounded-xl bg-[#141824] border border-gray-800 px-4 py-3"
              >
                <p className="text-xs text-gray-500">{s.label}</p>
                <p className={`text-2xl font-bold mt-0.5 ${s.color}`}>
                  {s.value}
                </p>
              </div>
            ))}
          </div>
        )}

        {mergeSuggestions.map((group) => (
          <MergeSuggestionBanner
            key={group.key}
            group={group}
            invoices={invoices}
            onMerge={() => mergeSuggestionGroup(group)}
            onDismiss={() => dismissSuggestion(group.key)}
          />
        ))}

        <div className="space-y-3">
          {invoices.map((inv, i) => (
            <InvoiceCard
              key={inv.id}
              inv={inv}
              index={i}
              v3Projects={v3Projects}
              vendors={vendors}
              subcontractors={subcontractors}
              mosByProject={mosByProject}
              billedMoIdsByProject={billedMoIdsByProject}
              onUpdate={updateInvoice}
              onRemove={removeInvoice}
              onLoadMOs={loadMOs}
              defaultTaxRate={defaultTaxRate}
              budgets={budgets}
              defaultBudgetId={defaultBudgetId}
              allInvoices={invoices.map((x, xi) => ({ ...x, index: xi }))}
              onMergeInto={(sourceIndex, targetIndex) =>
                mergeInvoices(targetIndex, sourceIndex)
              }
              mergeSourceId={mergeSourceId}
              setMergeSourceId={setMergeSourceId}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
